import React, { useEffect, useState } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { RouterProvider } from 'react-router5'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './queryClient'
import Box from '@mui/material/Box'

import createRouter from './router'
import { RouterContextProvider } from './contexts/router'
import { UnsavedChangesContextProvider } from './contexts/unsavedChanges'
import { SnackbarContextProvider } from './contexts/snackbar'
import { LoadingContextProvider } from './contexts/loading'
import { AccountContextProvider } from './contexts/account'
import { ConfigSchemaProvider } from './contexts/configSchema'

import Layout from './pages/Layout'
import { PageToolsProvider } from './pageTools/PageToolsProvider'
import { HelpProvider } from './help/HelpProvider'
import { WalkthroughProvider } from './help/WalkthroughProvider'
import Loading from './components/system/Loading'
import ErrorBoundary from './components/system/ErrorBoundary'
import UpdateBanner from './components/system/UpdateBanner'
import routes, { DEFAULT_ROUTE } from './routes'
import theme from './theme'
import useAccount from './hooks/useAccount'
import useRouter from './hooks/useRouter'


const AppRouter = createRouter(routes, { defaultRouteName: 'home' })
const {
  router,
  initialize,
  setUser,
  startMiddleware,
  RenderPage,
} = AppRouter

const InitializationGate: React.FC<React.PropsWithChildren> = ({ children }) => {
  const account = useAccount()
  const routerHook = useRouter()
  const [booted, setBooted] = useState(false)

  useEffect(() => {
    const doAsync = async () => {
      if (account.initialized) {
        const initialRouterState = await initialize(account.user)
        const publicRoutes = ['home', 'forgot-password', 'reset-password', 'shared-dashboard', 'shared-artifact', 'help', 'help-section']

        if(account.user) {
          // Wait for job initialization to complete before marking as done
          await account.initializeJob(initialRouterState.params.customer, initialRouterState.params.job)
        } else if(!publicRoutes.includes(initialRouterState.name)) {
          account.setPostLoginRoute({
            name: initialRouterState.name,
            params: initialRouterState.params,
          })
          routerHook.navigate('home')
        }

        // Mark initialization as complete (enables full middleware including requireUser)
        startMiddleware()
        setBooted(true)
      }
    }

    doAsync()
  }, [account.initialized])

  if (!account.initialized || !booted) {
    // Full viewport height container to center loading indicator vertically
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Loading message="Initializing application..." />
      </Box>
    )
  }

  return <>{children}</>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <UpdateBanner />
        <RouterProvider router={router}>
          <RouterContextProvider router={AppRouter} setUser={setUser}>
            <UnsavedChangesContextProvider>
              <SnackbarContextProvider>
                <LoadingContextProvider>
                  <AccountContextProvider>
                    <ConfigSchemaProvider>
                      <InitializationGate>
                        <ErrorBoundary>
                          <PageToolsProvider>
                            {/* The Help pane (System context PR B): app-wide, so its
                                conversation and a tour survive moving between pages. */}
                            <HelpProvider>
                              {/* Walkthroughs (20 Oct 2026): the click-through card on
                                  the same tour engine, from help/walkthroughs. */}
                              <WalkthroughProvider>
                                <Layout>
                                  <React.Suspense fallback={<Loading message="Loading page..." />}>
                                    <RenderPage />
                                  </React.Suspense>
                                </Layout>
                              </WalkthroughProvider>
                            </HelpProvider>
                          </PageToolsProvider>
                        </ErrorBoundary>
                      </InitializationGate>
                    </ConfigSchemaProvider>
                  </AccountContextProvider>
                </LoadingContextProvider>
              </SnackbarContextProvider>
            </UnsavedChangesContextProvider>
          </RouterContextProvider>
        </RouterProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
